import { InteractionStatus } from "@azure/msal-browser";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { useEffect } from "react";
import { Navigate, RouteObject, redirect } from "react-router-dom";

import NotFound from "@/app/pages/404";
import Settings from "@/app/pages/accountManagement/Settings";
import Portfolio from "@/app/pages/Portfolio";
import ProjectBaseline, {
  ProjectBaselineRouteProps,
} from "@/app/pages/strategyDefinition/ProjectBaseline";
import ProjectSchedule from "@/app/pages/strategyDefinition/ProjectSchedule";
import ProjectScope from "@/app/pages/strategyDefinition/ProjectScope";
import Scenarios from "@/app/pages/strategyDefinition/Scenarios";
import StrategyDefinitionProvider from "@/app/pages/strategyDefinition/StrategyDefinitionProvider";
import { getAllowances } from "@/features/allowance";
import { getCostDataService } from "@/features/cost";
import { getProjectBaselineParameters } from "@/features/parametric";
import { getPortfolio } from "@/features/portfolio";
import {
  loader as projectScopeLoader,
  getDeliverableById,
} from "@/features/projectScope";
import { getReserves } from "@/features/reserves";
import { getProjectBaseRisks } from "@/features/risk";
import MainNavigationLayout from "@/layouts/MainNavigationLayout";
import StrategyDefinitionLayout from "@/layouts/StrategyDefinitionLayout";
import { loginRequest } from "@/shared/config/authConfig";
import getUserFromGraph from "@/shared/store/authThunks";
import { useAppDispatch } from "@/shared/store/hooks";
import { ParameterModels } from "@/shared/types/parameterModels";

/**
 * A component that renders the given component only if the user is authenticated.
 * If the user is not authenticated, it redirects to the login page.
 * @param {JSX.Element} component - The component to render if the user is authenticated.
 * @returns {JSX.Element} The protected component or a redirect to the login page.
 */
const ProtectedRoute = ({ component }: { component: JSX.Element }): JSX.Element => {
  const dispatch = useAppDispatch();

  const isAuthenticated = useIsAuthenticated();
  const { instance, inProgress } = useMsal();

  useEffect(() => {
    void instance.handleRedirectPromise();

    if (inProgress === InteractionStatus.None && !isAuthenticated) {
      void instance.loginPopup(loginRequest);
    } else {
      void dispatch(getUserFromGraph());
    }
  }, [dispatch, inProgress, instance, isAuthenticated]);

  return component;
};

/**
 * Returns an array of RouteObjects for the private routes of the application.
 * These routes require authentication to access.
 * @returns {RouteObject[]} An array of RouteObjects representing the private routes.
 */
export default function PrivateRoutes(): RouteObject[] {
  return [
    {
      path: "/",
      element: <MainNavigationLayout />,
      children: [
        {
          index: true,
          element: <ProtectedRoute component={<Navigate to="/portfolio" replace />} />,
          errorElement: <div>Oops! There was an error in the Portfolio.</div>,
        },
        {
          path: "login",
          element: <ProtectedRoute component={<Navigate to="/portfolio" replace />} />,
          errorElement: <div>Oops! There was an error in the Portfolio.</div>,
        },
        {
          path: "portfolio",
          element: <ProtectedRoute component={<Portfolio />} />,
          errorElement: <div>Oops! There was an error in the Portfolio.</div>,
          loader: async () => getPortfolio(),
        },
        {
          path: "settings",
          element: <ProtectedRoute component={<Settings />} />,
        },
        {
          path: "strategy-definition/:id",
          element: (
            <ProtectedRoute
              component={
                <StrategyDefinitionProvider>
                  <StrategyDefinitionLayout />
                </StrategyDefinitionProvider>
              }
            />
          ),
          errorElement: <div>Oops! There was an error in Strategy Definition.</div>,
          children: [
            {
              path: "project-scope",
              element: <ProtectedRoute component={<ProjectScope />} />,
              errorElement: <div>Oops! There was an error in Project Scope.</div>,
              loader: projectScopeLoader,
            },
            {
              path: "project-schedule",
              element: <ProtectedRoute component={<ProjectSchedule />} />,
              errorElement: <div>Oops! There was an error in Project Schedule.</div>,
            },
            {
              path: "project-baseline/:outcome?/:tab?",
              element: <ProtectedRoute component={<ProjectBaseline />} />,
              errorElement: <div>Oops! There was an error in Project Baseline.</div>,
              loader: async ({ params }) => {
                // This object helps us pass the appropriate types to the <ProjectBaseline /> component
                // allowing us to pass the expected data and columns types to Parametric, Cost, Risk components
                const wbsEntryBaselineData: ProjectBaselineRouteProps = {
                  parametric: [],
                  cost: [],
                  risks: [],
                  allowances: [],
                  reserves: [],
                };

                // Check if we have the basic required params
                if (!params.id) {
                  return wbsEntryBaselineData;
                }

                // Get the outcome ID - this will always exist since we need to know which deliverable/outcome we're viewing
                const outcomeId = params.outcome ?? params.id;

                // Fetch the WBS entry to determine its actual level
                let currentLevel = 0;
                try {
                  const wbsEntry = await getDeliverableById(outcomeId);
                  if (wbsEntry) {
                    currentLevel = wbsEntry.level;
                  }
                } catch (error) {
                  // If we can't get the level, assume level 0
                  currentLevel = 0;
                }

                // Determine valid tabs based on the actual WBS level
                const level0Tabs = ["parametric", "reserves"];
                const level3Tabs = ["parametric", "cost", "allowance", "risk"];
                const validTabs = currentLevel === 0 ? level0Tabs : level3Tabs;

                // If no tab is selected, redirect to parametric
                if (!params.tab) {
                  return redirect(`../project-baseline/${outcomeId}/parametric`);
                }

                // If the selected tab is not valid for the current level, redirect to parametric
                if (!validTabs.includes(params.tab)) {
                  return redirect(`../project-baseline/${outcomeId}/parametric`);
                }

                // Always load parametric data (needed for BaselineOverview on all tabs)
                const parameters: ParameterModels[] =
                  await getProjectBaselineParameters(outcomeId);
                wbsEntryBaselineData.parametric = parameters;

                // Load data based on level and tab
                if (params.tab === "reserves" && currentLevel === 0) {
                  // Reserves are at project level (level 0) - use project ID
                  const reserves = await getReserves(params.id);
                  wbsEntryBaselineData.reserves = reserves;
                }

                // The following tabs require level 3 (sub-outcome level)
                if (currentLevel !== 0 && params.outcome) {
                  if (params.tab === "risk") {
                    const risks = await getProjectBaseRisks(params.outcome);
                    wbsEntryBaselineData.risks = risks;
                  }
                  if (params.tab === "cost") {
                    wbsEntryBaselineData.cost = await getCostDataService(
                      params.id,
                      params.outcome
                    );
                  }
                  if (params.tab === "allowance") {
                    const allowances = await getAllowances(params.outcome);
                    wbsEntryBaselineData.allowances = allowances;
                  }
                }

                return wbsEntryBaselineData;
              },
            },
            {
              path: "project-scenarios/:outcome?/:tab?",
              element: <ProtectedRoute component={<Scenarios />} />,
              errorElement: <div>Oops! There was an error in Procurement Scenarios.</div>,
            },
          ],
        },
      ],
    },
    {
      path: "*",
      element: <NotFound />,
    },
  ];
}
